import { useContext, useState, useEffect } from "react";
import UserContext from "../../../context/UserContext";
import UserApi from "../../../api/UserApi";
import { resolveUserProfileImg } from "../../../util/ProfileUtil";

const getDiffData = (o, n) => {
    const diff = {};
    for (const key in n) {
        if (n.hasOwnProperty(key)) {
            if (o[key] !== n[key]) {
                diff[key] = n[key];
            }
        }
    }
    return diff;
};

const MyInfo = () => {
    const { user, updateUserInfo } = useContext(UserContext);
    const [userInfo, setUserInfo] = useState({ ...user });
    const [isValidNicknm, setIsValidNicknm] = useState(false);
    const [profileImg, setProfileImg] = useState(userInfo.userProfileImg);
    const [profileImgFile, setProfileImgFile] = useState(null);

    const handleUserInfoChg = (e) => {
        const { name, value } = e.target;
        const newUserInfo = { ...userInfo };

        if (name === "userPhone" || name === "userExpertPhone") {
            const numericValue = value.replace(/[^0-9]/g, "");
            newUserInfo[name] = numericValue;
        } else {
            newUserInfo[name] = value;
        }

        setUserInfo(newUserInfo);
        if (name === "userNicknm") setIsValidNicknm(false);
    };

    const handelUpdateBtn = async (e) => {
        e?.preventDefault?.();

        let userProfileImg = "";
        let profileChanged = false;
        if (profileImgFile) {
            const formData = new FormData();
            const originalExtension = profileImgFile.name.split(".").pop();
            const renamedFile = new File(
                [profileImgFile],
                `${userInfo.userId}.${originalExtension}`,
                {
                    type: profileImgFile.type,
                    lastModified: profileImgFile.lastModified,
                }
            );

            formData.append("userProfileImg", renamedFile);
            formData.append("userId", userInfo.userId);

            const rslt = await UserApi.updateUserProfileImg(formData);
            userProfileImg = rslt.imagePath || rslt.imageUrl;
            profileChanged = true;
        }

        if (userInfo.userNicknm !== user.userNicknm && !isValidNicknm) {
            return alert("닉네임 중복 체크를 해주세요.");
        }
        if (userInfo.userPw && userInfo.userPw !== userInfo.userConfirmPw) {
            return alert("비밀번호 확인이 일치하지 않습니다.");
        }
        if (userProfileImg) userInfo.userProfileImg = userProfileImg;

        const diffData = getDiffData(user, userInfo);
        if (Object.keys(diffData).length === 0 && !profileChanged) {
            return alert("변경된 정보가 없습니다.");
        }

        if (Object.keys(diffData).length > 0) {
            const rslt = await UserApi.updateUserInfo(diffData);
            if (rslt.code === "success") {
                alert("개인 정보가 변경 되었습니다.");
                updateUserInfo();
                return;
            }
        } else if (profileChanged) {
            alert("프로필 이미지가 변경 되었습니다.");
            window.location.reload();
            return;
        }

        alert("정보 변경에 실패하였습니다.");
    };

    const handleNicknmDuplClick = async () => {
        if (!userInfo.userNicknm) return alert("입력된 닉네임이 없습니다.");
        const res = await UserApi.duplNicknmCheck(userInfo.userNicknm);
        const msg = res.valid ? "사용 가능한 닉네임입니다." : "사용할 수 없는 닉네임임입니다.";
        alert(msg);
        setIsValidNicknm(res.valid);
    };

    const handleProfileImgChg = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== "image/png" && file.type !== "image/jpg" && file.type !== "image/jpeg") {
            alert("파일 형식은 PNG, JPG 파일만 가능합니다.");
            return;
        }

        if (file.size > 5 * 1024) {
            alert("파일 크기는 5KB 이하만 가능합니다.");
            return;
        }

        setProfileImgFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => {
            setProfileImg(ev.target.result);
        };
        reader.readAsDataURL(file);
    };

    useEffect(() => {
        setUserInfo({ ...user });
        setProfileImg(user?.userProfileImg);
        setProfileImgFile(null);
    }, [user]);

    const profileImgSrc = profileImg?.startsWith('data:')
        ? profileImg
        : resolveUserProfileImg({
            ...userInfo,
            userProfileImg: profileImg || userInfo.userProfileImg,
        });

    return (
        <section className="mypage-container">
            <form className="profile-form" onSubmit={handelUpdateBtn}>
                <div className="profile-form-photo">
                    <img src={profileImgSrc} alt="프로필 이미지" />
                    <label className="profile-form-photo-button" htmlFor="profileImgUpload">
                        <i className="icon icon-camera"></i>
                        <input
                            type="file"
                            id="profileImgUpload"
                            name="userProfileImg"
                            accept="image/*"
                            onChange={handleProfileImgChg}
                        />
                        <span>프로필 이미지 변경</span>
                    </label>
                </div>

                <div className="profile-form-field">
                    <label htmlFor="profileId">아이디</label>
                    <input
                        id="profileId"
                        type="text"
                        value={userInfo.userId || ""}
                        readOnly
                    />
                </div>

                <div className="profile-form-field">
                    <label htmlFor="profileName">이름</label>
                    <input
                        id="profileName"
                        type="text"
                        value={userInfo.userNm || ""}
                        readOnly
                    />
                </div>

                <div className="profile-form-field">
                    <label htmlFor="profileEmail">이메일</label>
                    <input
                        id="profileEmail"
                        type="email"
                        value={userInfo.userEmail || ""}
                        readOnly
                    />
                </div>

                <div className="profile-form-field">
                    <label htmlFor="profileNickname">닉네임</label>
                    <div className="profile-form-inline">
                        <input
                            id="profileNickname"
                            type="text"
                            name="userNicknm"
                            value={userInfo.userNicknm || ""}
                            onChange={handleUserInfoChg}
                            placeholder="닉네임을 입력해주세요"
                        />
                        <button type="button" onClick={handleNicknmDuplClick}>
                            중복확인
                        </button>
                    </div>
                </div>

                <div className="profile-form-field">
                    <label htmlFor="profilePassword">비밀번호</label>
                    <input
                        id="profilePassword"
                        type="password"
                        name="userPw"
                        value={userInfo.userPw || ""}
                        placeholder="비밀번호"
                        onChange={handleUserInfoChg}
                    />
                    {userInfo.userPw && userInfo.userPw.length < 8 && (
                        <p className="input-hint">비밀번호는 8자리 이상 입력하세요.</p>
                    )}
                </div>

                <div className="profile-form-field">
                    <label htmlFor="profilePasswordConfirm">비밀번호 확인</label>
                    <input
                        id="profilePasswordConfirm"
                        type="password"
                        name="userConfirmPw"
                        value={userInfo.userConfirmPw || ""}
                        placeholder="비밀번호 확인"
                        onChange={handleUserInfoChg}
                    />
                </div>

                <div className="profile-form-field">
                    <label htmlFor="profileContact">연락처</label>
                    <input
                        id="profileContact"
                        type="text"
                        name="userPhone"
                        value={userInfo.userPhone || ""}
                        onChange={handleUserInfoChg}
                        maxLength={11}
                        placeholder="01012345678"
                    />
                </div>

                <div className="profile-form-field">
                    <label htmlFor="profileIntro">전문가 소개</label>
                    <textarea
                        id="profileIntro"
                        name="user_expert_describe"
                        value={userInfo.user_expert_describe || ""}
                        onChange={handleUserInfoChg}
                        placeholder="전문가로서의 소개를 작성해주세요"
                    />
                </div>

                <div className="btn_area">
                    <button type="submit" className="btn btn-primary">
                        <span>수정하기</span>
                    </button>
                </div>
            </form>
        </section>
    );
};

export default MyInfo;
